import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { BokkzAlgDataService, BokkzVpbDataService, BokkzVpbLogic, CorporateTaxEntity, IdentityProviderStatus, KolommenbalansService, PmClient, VpbRekenService } from '@snelstart-ventures/fiscaal/domain-logic';
import { FormBuilder } from '@angular/forms';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { catchError, combineLatest, defer, distinctUntilChanged, EMPTY, filter, from, map, merge, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { ToastService } from '@snelstart/ui-toast';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { ButtonConfiguration, SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { SharedUiLoadingModule } from '@snelstart/ui-loading';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { createAdministrationFormGroup } from './administratie-koppelen.form';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-administratie-koppelen',
    templateUrl: './administratie-koppelen.component.html',
    styleUrls: ['./administratie-koppelen.component.scss'],
    imports: [
        SharedUiDetailsModule,
        SharedUiFormControlsModule,
        SharedUiButtonModule,
        SharedUiAlertModule,
        SharedUiLoadingModule,
        CommonModule,
    ],
})
export class AdministratieKoppelenComponent implements OnInit, OnDestroy {
    algDataService = inject(BokkzAlgDataService);
    vpbDataService = inject(BokkzVpbDataService);
    vpbRekenService = inject(VpbRekenService);
    vpbService = inject(BokkzVpbLogic);
    kolommenbalansService = inject(KolommenbalansService);

    pmClient = inject(PmClient);
    toastService = inject(ToastService);
    formBuilder = inject(FormBuilder);
    destroyRef = inject(DestroyRef);

    formGroup = createAdministrationFormGroup(this.formBuilder);

    client$ = this.algDataService.client.pipe(
        filter((client) => !!client?.id),
        distinctUntilChanged((previousClient, currentClient) => currentClient.id === previousClient.id),
        tap((client) => console.debug(`Client: "${client.name}"`, client)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    corporateTax$ = this.vpbDataService.corporateTaxEntity.pipe(
        filter((corporateTax) => !!corporateTax?.id),
        distinctUntilChanged((previousCorporateTax, currentCorporateTax) => currentCorporateTax.id === previousCorporateTax.id),
        tap((corporateTax) => console.debug(`CorporateTax: ${corporateTax.name}`, corporateTax)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    administrations$ = this.client$.pipe(
        map((client) => client.adminEntities ?? []),
        tap((administrations) => {
            if (administrations.length === 0) {
                console.debug('No administrations loaded');
            }

            if (administrations.length > 0) {
                console.debug(`Loaded ${administrations.length} administrations`);
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    administrationOptions$ = this.administrations$.pipe(
        map((administrations) =>
            administrations.map<SelectFormControlItem<number>>((administration) => ({
                value: administration.id!,
                label: administration.name!,
            })),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    selectedAdministrationId$ = merge(
        defer(() => of(this.formGroup.controls.administrationId.value)),
        this.formGroup.controls.administrationId.valueChanges,
    ).pipe(
        distinctUntilChanged(),
        tap((selectedAdministrationId) => console.debug(`Selected AdministrationId: ${selectedAdministrationId}`)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    selectedAdministration$ = combineLatest([this.administrations$, this.selectedAdministrationId$]).pipe(
        map(
            ([administrations, selectedAdministrationId]) =>
                administrations.find((administration) => administration.id === selectedAdministrationId) ?? null,
        ),
        distinctUntilChanged((previousAdministration, currentAdministration) => currentAdministration?.id === previousAdministration?.id),
        tap((administration) => {
            if (administration) {
                console.debug(`Administration: "${administration.name}"`, administration);
            } else {
                console.debug('No administration selected');
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    identityProvider$ = this.selectedAdministration$.pipe(
        switchMap((administration) =>
            administration?.providerId ? this.pmClient.getIdentityProvider$(administration!.providerId) : of(null),
        ),
        distinctUntilChanged(
            (previousIdentityProvider, currentIdentityProvider) => currentIdentityProvider?.id === previousIdentityProvider?.id,
        ),
        tap((identityProvider) => {
            if (identityProvider) {
                console.debug(`Identity Provider: "${identityProvider.name}"`, identityProvider);
            } else {
                console.debug('No identity provider loaded');
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    identityProviderStatus$ = combineLatest([this.selectedAdministration$, this.identityProvider$]).pipe(
        switchMap(([selectedAdministration, identityProvider]) => {
            if (selectedAdministration && identityProvider && selectedAdministration.providerId === identityProvider.id) {
                if (identityProvider.requiresEnvironment) {
                    if (selectedAdministration.environmentId) {
                        return this.pmClient.getIdentityProviderEnvironmentStatus$(
                            identityProvider.id,
                            selectedAdministration.environmentId,
                        );
                    }

                    return of({ id: identityProvider.id, name: identityProvider.name, authenticated: false } as IdentityProviderStatus);
                }

                return this.pmClient.getIdentityProviderStatus$(identityProvider.id);
            }

            return of(null);
        }),
        tap((identityProviderStatus) => {
            if (identityProviderStatus) {
                console.debug(`Loaded "${identityProviderStatus.name}" identity provider status`, identityProviderStatus);
            } else {
                console.debug('No identity provider status loaded');
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    importSnelStartBalanceButtonConfiguration = ButtonConfiguration.create({
        canExecute: () => this.canImportSnelStartBalance$,
        onClick: () => this.importSnelStartBalance$,
        onError: (error) => console.error(error),
    });

    canImportSnelStartBalance$ = this.selectedAdministration$.pipe(
        take(1),
        map((selectedAdministration) => {
            if (!selectedAdministration) {
                this.toastService.showErrorMessage('Er is geen administratie geselecteerd.');
                return false;
            }

            return true;
        }),
    );

    importSnelStartBalance$ = combineLatest([this.client$, this.corporateTax$, this.selectedAdministration$]).pipe(
        take(1),
        switchMap(([client, corporateTax, selectedAdministration]) => {
            if (!client || !corporateTax || !selectedAdministration) {
                return EMPTY;
            }

            console.debug('Importing SnelStart administration', client, corporateTax, selectedAdministration);

            return from(
                this.kolommenbalansService.getUpdatedAangifteMetAdministratie(
                    selectedAdministration!.administrationId!,
                    client.sstRelationId!,
                    corporateTax,
                    false,
                    client.sstPrimaryAdminId!,
                ),
            ).pipe(map((corporateTax) => [corporateTax as CorporateTaxEntity, selectedAdministration] as const));
        }),
        tap(([corporateTax, selectedAdministration]) => {
            console.debug('Updating CorporateTaxEntity', corporateTax);

            if (this.vpbDataService.getIsVerzoek(corporateTax)) {
                this.vpbDataService.removeBalansActivaPassiva(corporateTax);
            }

            if (corporateTax.aangifte?.balansEnResultatenrekening != null) {
                corporateTax.aangifte.balansEnResultatenrekening.administratieId = selectedAdministration.administrationId!;
            }

            this.vpbDataService.updateCorporateTaxEntity(corporateTax);
        }),
        switchMap(([corporateTax, selectedAdministration]) => {
            console.debug('Calculate CorporateTaxEntity VpbAangifte', corporateTax.aangifte);

            return this.vpbRekenService
                .getCorporateTaxCalculation(corporateTax.aangifte!)
                .pipe(map((vpbBerekening) => [corporateTax, vpbBerekening, selectedAdministration] as const));
        }),
        tap(([corporateTax, vpbBerekening, selectedAdministration]) => {
            console.debug('Update VpbBerekening', corporateTax, vpbBerekening, selectedAdministration);

            this.vpbDataService.updateVpbBerekening(vpbBerekening);

            this.toastService.showSuccessMessage(
                `De administratie ${corporateTax.year} van '${selectedAdministration.name}' is succesvol ingelezen.`,
            );
        }),
    );

    importBalanceProviderBalanceButtonConfiguration = ButtonConfiguration.create({
        canExecute: () => this.canImportBalanceProviderBalance$,
        onClick: () => this.importBalanceProviderBalance$,
        onError: (error) => console.error(error),
    });

    canImportBalanceProviderBalance$ = combineLatest([this.selectedAdministration$, this.identityProviderStatus$]).pipe(
        take(1),
        map(([selectedAdministration, identityProviderStatus]) => {
            if (!selectedAdministration) {
                this.toastService.showErrorMessage('Er is geen administratie geselecteerd.');
                return false;
            }

            const authenticated = !!identityProviderStatus?.authenticated;

            if (!authenticated) {
                this.toastService.showErrorMessage('U bent niet geautoriseerd om deze administratie in te lezen.');
            }

            return authenticated;
        }),
    );

    importBalanceProviderBalance$ = combineLatest([this.client$, this.corporateTax$, this.selectedAdministration$]).pipe(
        take(1),
        switchMap(([client, corporateTax, selectedAdministration]) => {
            if (!client || !corporateTax || !selectedAdministration?.providerId) {
                return EMPTY;
            }

            console.debug(`Importing ${selectedAdministration.name} administration`, client, corporateTax, selectedAdministration);

            if (selectedAdministration.environmentId) {
                return this.pmClient
                    .postBalanceProviderBalancesEnvironment(
                        client.sstRelationId!,
                        selectedAdministration.providerId!,
                        selectedAdministration.environmentId!,
                        selectedAdministration.administrationId!,
                        false,
                        corporateTax,
                    )
                    .pipe(map((corporateTax) => [corporateTax as CorporateTaxEntity, selectedAdministration] as const));
            } else {
                return this.pmClient
                    .postBalanceProviderBalances(
                        client.sstRelationId!,
                        selectedAdministration.providerId!,
                        selectedAdministration.administrationId!,
                        false,
                        corporateTax,
                    )
                    .pipe(map((corporateTax) => [corporateTax as CorporateTaxEntity, selectedAdministration] as const));
            }
        }),
        tap(([corporateTax, selectedAdministration]) => {
            console.debug('Aangifte berekend: ', corporateTax);

            if (corporateTax.aangifte?.balansEnResultatenrekening) {
                corporateTax.aangifte.balansEnResultatenrekening.administratieId = selectedAdministration.administrationId ?? undefined;
            }

            this.vpbDataService.updateCorporateTaxEntity(corporateTax);

            this.toastService.showSuccessMessage(
                `De administratie ${corporateTax.year} van '${selectedAdministration.name}' is succesvol ingelezen.`,
            );

            return corporateTax;
        }),
        catchError((error) => {
            console.error(error);
            this.toastService.showErrorMessage(
                'Het is helaas niet gelukt de administratie in te lezen in de aangifte. Je kunt wel verder met de aangifte door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',
            );
            return EMPTY;
        }),
    );

    ngOnInit(): void {
        this.administrations$
            .pipe(
                map((administrationEntities) => (administrationEntities.length === 1 ? (administrationEntities[0] ?? null) : null)),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((administrationEntity) => {
                if (administrationEntity?.id ?? null !== this.formGroup.controls.administrationId.value) {
                    console.debug(`Selecting "${administrationEntity?.name}"`, administrationEntity);
                    this.formGroup.controls.administrationId.setValue(administrationEntity?.id ?? null);
                }
            });
    }

    ngOnDestroy(): void {
        if (!this.formGroup.dirty) {
            return;
        }

        this.corporateTax$
            .pipe(
                take(1),
                tap((corporateTax) => this.vpbService.postCorporateTaxEntity(corporateTax, false, false)),
            )
            .subscribe({
                error: (error: Error) => console.error('Error saving income tax.', error),
            });
    }
}
